import type { HandoverV2Row } from '../services/deliveries-v2.service'

/**
 * El vocabulario de Postventa en la web v2.
 *
 * TRES estados, no dos. `not_started` es una negociación CERRADA sin acta: el
 * estado más numeroso del listado y el único desde el que se puede empezar una
 * postventa. v1 no sabía nombrarlo porque sólo miraba `isDelivered`.
 *
 * Sin `enum`: const object + union type, como manda la convención del monorepo.
 */
export const HANDOVER_STATUS = {
  NotStarted: 'not_started',
  Pending: 'pending',
  Completed: 'completed'
} as const

export type HandoverStatusValue = (typeof HANDOVER_STATUS)[keyof typeof HANDOVER_STATUS]

/** Copy de UI en tuteo neutro, nunca voseo (regla 8 del monorepo). */
export const HANDOVER_STATUS_LABELS: Record<HandoverStatusValue, string> = {
  not_started: 'Sin iniciar',
  pending: 'En proceso',
  completed: 'Entregado'
}

export const HANDOVER_STATUS_COLORS: Record<HandoverStatusValue, string> = {
  not_started: 'bg-muted text-muted-foreground',
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
}

/** Nombres visibles de las columnas, para el selector de visibilidad. */
/** Estados del expediente de Créditos que Postventa puede mostrar. */
export const CREDIT_STATUS = {
  InProgress: 'in_progress',
  Approved: 'approved',
  Cancelled: 'cancelled'
} as const

export type CreditStatusValue = (typeof CREDIT_STATUS)[keyof typeof CREDIT_STATUS]

/** Copy en tuteo neutro. `approved` → «Listo para entrega» (SPEC-11). */
export const CREDIT_STATUS_LABELS: Record<CreditStatusValue, string> = {
  in_progress: 'En trámite',
  approved: 'Listo para entrega',
  cancelled: 'Cancelado'
}

export const CREDIT_STATUS_COLORS: Record<CreditStatusValue, string> = {
  in_progress: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  cancelled: 'bg-muted text-muted-foreground'
}

export const DELIVERY_COLUMN_NAMES: Record<string, string> = {
  code: 'Negociación',
  property: 'Inmueble',
  clients: 'Cliente',
  project: 'Proyecto',
  status: 'Entrega',
  creditStatus: 'Crédito',
  advisor: 'Asesor',
  closedAt: 'Cerrada',
  deliveryCode: 'Acta',
  scheduledDate: 'Programada',
  completedAt: 'Entregada el'
}

export type HandoverRow = HandoverV2Row

/**
 * Fecha de CALENDARIO a texto.
 *
 * Llega como `YYYY-MM-DD` y se formatea partiendo el string, sin construir un `Date`:
 * `new Date('2026-08-15')` lo interpreta como medianoche UTC y al leerlo en hora
 * local corre el día entero hacia atrás para América. Una entrega programada el 15
 * se mostraría el 14.
 */
export function formatLocalDate(value: string | null | undefined): string {
  if (!value) return '—'
  const [year, month, day] = value.split('-')
  if (!year || !month || !day) return '—'
  return `${day}/${month}/${year}`
}

/** Instante a fecha corta local. Acá sí es un momento en el tiempo, no un día. */
export function formatInstantDate(value: string | null | undefined): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
